import { apiClient } from '@/api/api'
import type {
  AttachOutfitCapsulesResponse,
  AvailableItemsResponse,
  CreateOutfitBody,
  CreateOutfitResponse,
  DeleteOutfitResponse,
  EditImpactBody,
  EditImpactResponse,
  OutfitListItem,
  OutfitListResponse,
  PatchOutfitBody,
  PatchOutfitResponse,
} from './types'
import { PLAYGROUND_PAGE_SIZE_MAX } from './wardrobe'

/** FastAPI reads repeated keys (`capsule_ids=1&capsule_ids=2`), not `capsule_ids[]`. */
export const REPEATED_QUERY_KEYS = { indexes: null } as const

export const getOutfits = async (pageNum = 1, pageSize = PLAYGROUND_PAGE_SIZE_MAX) => {
  return apiClient.get<OutfitListResponse>('/playground/outfits', {
    params: { page_num: pageNum, page_size: pageSize },
  })
}

/**
 * `GET /playground/outfits/{outfit_id}` (BE-130) — one outfit in the shape the
 * list serves it; 404 when it does not exist, 403 when it is someone else's.
 */
export const getOutfit = async (outfitId: number) => {
  return apiClient.get<OutfitListItem>(`/playground/outfits/${outfitId}`)
}

export const createOutfit = async (body: CreateOutfitBody) => {
  return apiClient.post<CreateOutfitResponse>('/playground/outfits', body)
}

export const patchOutfit = async (outfitId: number, body: PatchOutfitBody) => {
  return apiClient.patch<PatchOutfitResponse>(`/playground/outfits/${outfitId}`, body)
}

/** Dry run of the PATCH — strategy, scope and the fork/warning modals (§2.4d). */
export const getOutfitEditImpact = async (outfitId: number, body: EditImpactBody) => {
  return apiClient.post<EditImpactResponse>(`/playground/outfits/${outfitId}/edit-impact`, body)
}

export const deleteOutfit = async (outfitId: number) => {
  return apiClient.delete<DeleteOutfitResponse>(`/playground/outfits/${outfitId}`)
}

export const attachOutfitToCapsules = async (outfitId: number, capsuleIds: number[]) => {
  return apiClient.post<AttachOutfitCapsulesResponse>(`/playground/outfits/${outfitId}/capsules`, {
    capsuleIds,
  })
}

export interface AvailableItemsParams {
  capsuleIds?: number[]
  includeMyWardrobe?: boolean
  includeCapsuleInventory?: boolean
  type?: number | null
  pageNum?: number
  pageSize?: number
}

export const getOutfitAvailableItems = async (
  outfitId: number,
  params: AvailableItemsParams = {},
) => {
  return apiClient.get<AvailableItemsResponse>(`/playground/outfits/${outfitId}/available-items`, {
    params: {
      page_num: params.pageNum ?? 1,
      page_size: params.pageSize ?? PLAYGROUND_PAGE_SIZE_MAX,
      include_my_wardrobe: params.includeMyWardrobe ?? true,
      include_capsule_inventory: params.includeCapsuleInventory ?? true,
      ...(params.capsuleIds?.length ? { capsule_ids: params.capsuleIds } : {}),
      ...(params.type != null ? { type: params.type } : {}),
    },
    paramsSerializer: REPEATED_QUERY_KEYS,
  })
}
